import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { friendGroupsAPI } from '../services/api';
import { FriendGroupWithMembers, User } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import './ListPanel.css';

type PanelState = 'minimized' | 'default' | 'expanded';

interface ListPanelProps {
  listId: number;
  onClose: () => void;
  onMemberClick?: (friendId: number) => void;
  onAddMember?: () => void;
  onHeightChange?: (heightVh: number) => void;
  onBack?: () => void;
  canGoBack?: boolean;
  refreshKey?: number; // Increment to trigger a refresh
  newMemberIds?: number[]; // IDs of newly added members (for animation)
  onNewMembersAnimated?: () => void; // Called after animation completes
}

const ListPanel: React.FC<ListPanelProps> = ({
  listId,
  onClose,
  onMemberClick,
  onAddMember,
  onHeightChange,
  onBack,
  canGoBack = false,
  refreshKey = 0,
  newMemberIds = [],
  onNewMembersAnimated
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const { t } = useLanguage();

  const [list, setList] = useState<FriendGroupWithMembers | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Description editing
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [description, setDescription] = useState('');
  const [isSavingDescription, setIsSavingDescription] = useState(false);

  // Panel state management
  const [panelState, setPanelState] = useState<PanelState>('default');
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);

  // Track which members are animating (newly added)
  const [animatingMemberIds, setAnimatingMemberIds] = useState<number[]>([]);

  // Track scroll position to restore after refresh
  const savedScrollPositionRef = useRef<number>(0);

  // Detect if desktop (disable drag on desktop)
  const isDesktop = () => window.innerWidth >= 769;

  // Heights for each state (in vh)
  const heights = useMemo(() => ({
    minimized: 30,
    default: 80,
    expanded: 90
  }), []);

  useEffect(() => {
    const loadList = async () => {
      try {
        // Don't show loading spinner on refresh (when adding members)
        const isRefresh = refreshKey > 0 && list !== null;
        if (!isRefresh) {
          setLoading(true);
        }

        const { group } = await friendGroupsAPI.getGroupWithMembers(listId);
        setList(group);
        setDescription(group.description || '');
        setError('');

        // Restore scroll position after refresh
        if (isRefresh && savedScrollPositionRef.current > 0) {
          requestAnimationFrame(() => {
            if (contentRef.current) {
              contentRef.current.scrollTop = savedScrollPositionRef.current;
            }
          });
        }
      } catch (err) {
        console.error('Failed to load list:', err);
        setError('Failed to load list');
      } finally {
        setLoading(false);
      }
    };

    loadList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId, refreshKey]);

  // Save scroll position when new members are about to be added
  useEffect(() => {
    if (newMemberIds.length > 0 && contentRef.current) {
      // Save current scroll position before refresh
      savedScrollPositionRef.current = contentRef.current.scrollTop;
    }
  }, [newMemberIds]);

  // Handle new members animation
  useEffect(() => {
    if (newMemberIds.length > 0) {
      // Start animation for new members
      setAnimatingMemberIds(newMemberIds);

      // Clear animation class after animation completes (slide + flash)
      const timer = setTimeout(() => {
        setAnimatingMemberIds([]);
        onNewMembersAnimated?.();
      }, 1000); // Match CSS animation duration (0.5s slide + 0.8s flash)

      return () => clearTimeout(timer);
    }
  }, [newMemberIds, onNewMembersAnimated]);

  // Save description
  const handleSaveDescription = async () => {
    if (!list) return;
    try {
      setIsSavingDescription(true);
      await friendGroupsAPI.update(list.id, {
        name: list.name,
        color: list.color,
        description: description.trim() || undefined
      });
      setList({ ...list, description: description.trim() || undefined });
      setIsEditingDescription(false);
    } catch (error) {
      console.error('Failed to save description:', error);
    } finally {
      setIsSavingDescription(false);
    }
  };

  // Drag handlers
  const handleDragStart = useCallback((clientY: number) => {
    if (isDesktop()) return;
    setIsDragging(true);
    setStartY(clientY);
    setCurrentY(clientY);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    handleDragStart(e.clientY);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    setCurrentY(e.clientY);
    const offset = e.clientY - startY;
    setDragOffset(offset);
  }, [isDragging, startY]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);
    const offset = currentY - startY;
    const threshold = 50;

    if (Math.abs(offset) > threshold) {
      if (offset > 0) {
        if (panelState === 'expanded') setPanelState('default');
        else if (panelState === 'default') setPanelState('minimized');
      } else {
        if (panelState === 'minimized') setPanelState('default');
        else if (panelState === 'default') setPanelState('expanded');
      }
    }

    setDragOffset(0);
    setStartY(0);
    setCurrentY(0);
  }, [isDragging, currentY, startY, panelState]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      handleDragStart(e.touches[0].clientY);
    }
  };

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (e.touches.length === 1) {
      if (!isDragging) return;
      setCurrentY(e.touches[0].clientY);
      const offset = e.touches[0].clientY - startY;
      setDragOffset(offset);
    }
  }, [isDragging, startY]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);
    const offset = currentY - startY;
    const threshold = 50;

    if (Math.abs(offset) > threshold) {
      if (offset > 0) {
        if (panelState === 'expanded') setPanelState('default');
        else if (panelState === 'default') setPanelState('minimized');
      } else {
        if (panelState === 'minimized') setPanelState('default');
        else if (panelState === 'default') setPanelState('expanded');
      }
    }

    setDragOffset(0);
    setStartY(0);
    setCurrentY(0);
  }, [isDragging, currentY, startY, panelState]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  const getPhotoUrl = (photoPath?: string) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('http')) return photoPath;
    return `${process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:5000'}${photoPath}`;
  };

  const getPanelHeight = () => {
    if (isDesktop()) return 100;

    const baseHeight = heights[panelState];
    if (isDragging && dragOffset !== 0) {
      const offsetVh = (dragOffset / window.innerHeight) * 100;
      return Math.max(20, Math.min(95, baseHeight - offsetVh));
    }
    return baseHeight;
  };

  // Notify parent of height changes
  useEffect(() => {
    if (onHeightChange) {
      let height: number;
      if (isDesktop()) {
        height = 100;
      } else {
        const baseHeight = heights[panelState];
        if (isDragging && dragOffset !== 0) {
          const offsetVh = (dragOffset / window.innerHeight) * 100;
          height = Math.max(20, Math.min(95, baseHeight - offsetVh));
        } else {
          height = baseHeight;
        }
      }
      onHeightChange(height);
    }
  }, [panelState, isDragging, dragOffset, onHeightChange, heights]);

  // Format address for display
  const formatAddress = (member: User): string => {
    if (!member.address) return '';
    const parts = [];
    if (member.address.city) parts.push(member.address.city);
    if (member.address.country) parts.push(member.address.country);
    return parts.join(', ');
  };

  if (loading) {
    return (
      <div
        className={`list-panel panel-${panelState}`}
        style={{ height: `${heights.default}vh` }}
      >
        <div className="loading-message">{t('common.loading')}...</div>
      </div>
    );
  }

  if (error || !list) {
    return (
      <div
        className={`list-panel panel-${panelState}`}
        style={{ height: `${heights.default}vh` }}
      >
        <div className="error-message">{error || t('friends.groups.notFound')}</div>
        <button onClick={onClose} className="btn-back">
          {t('common.close')}
        </button>
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      className={`list-panel panel-${panelState} ${isDragging ? 'dragging' : ''}`}
      style={{
        height: `${getPanelHeight()}vh`,
        transition: isDragging ? 'none' : 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    >
      {/* Drag Handle */}
      <div
        ref={handleRef}
        className="panel-drag-handle"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div className="handle-indicator"></div>
      </div>

      {/* Back Button (when history exists) */}
      {canGoBack && onBack && (
        <button className="btn-back-panel" onClick={onBack} title="Retour">
          ←
        </button>
      )}

      {/* Close Button */}
      <button className="btn-close-panel" onClick={onClose}>
        ✕
      </button>

      {/* Scrollable Content */}
      <div
        ref={contentRef}
        className={`panel-content ${panelState === 'expanded' ? 'scrollable' : ''}`}
      >
        {/* Header */}
        <div className="list-header">
          <div
            className="list-color-indicator"
            style={{ backgroundColor: list.color || '#4a90e2' }}
          />
          <h1 className="list-title">{list.name}</h1>
          <span className="list-member-count">
            {list.members.length} {list.members.length === 1 ? t('friends.groups.member') : t('friends.groups.members')}
          </span>
        </div>

        {/* Description Section */}
        <div className="list-description-section">
          {isEditingDescription ? (
            <div className="description-editor">
              <textarea
                className="description-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('friends.groups.descriptionPlaceholder')}
                rows={3}
              />
              <div className="description-actions">
                <button
                  className="btn-save-description"
                  onClick={handleSaveDescription}
                  disabled={isSavingDescription}
                >
                  {isSavingDescription ? t('common.saving') : t('common.save')}
                </button>
                <button
                  className="btn-cancel-description"
                  onClick={() => {
                    setDescription(list.description || '');
                    setIsEditingDescription(false);
                  }}
                  disabled={isSavingDescription}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div className="description-display" onClick={() => setIsEditingDescription(true)}>
              {list.description ? (
                <p className="description-text">{list.description}</p>
              ) : (
                <p className="description-placeholder">{t('friends.groups.addDescription')}</p>
              )}
            </div>
          )}
        </div>

        {/* Members List */}
        <div className="list-members-section">
          <h2 className="section-title">{t('friends.groups.members')}</h2>

          {list.members.length === 0 ? (
            <div className="empty-members">
              <p>{t('friends.groups.noMembers')}</p>
            </div>
          ) : (
            <div className="members-list">
              {list.members.map((member) => (
                <div
                  key={member.id}
                  className={`member-card ${animatingMemberIds.includes(member.id) ? 'newly-added' : ''}`}
                  onClick={() => onMemberClick?.(member.id)}
                >
                  <div className="member-photo-container">
                    {member.profile_photo ? (
                      <img
                        src={getPhotoUrl(member.profile_photo) || undefined}
                        alt={`${member.first_name} ${member.last_name}`}
                        className="member-photo"
                      />
                    ) : (
                      <div className="member-photo-placeholder">
                        {member.first_name?.[0]}{member.last_name?.[0]}
                      </div>
                    )}
                  </div>
                  <div className="member-info">
                    <span className="member-name">
                      {member.first_name} {member.last_name}
                    </span>
                    {formatAddress(member) && (
                      <span className="member-address">
                        {formatAddress(member)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Member Button */}
          <button className="btn-add-member" onClick={onAddMember}>
            + {t('friends.groups.addMembers')}
          </button>
        </div>

        {/* Panel hints */}
        {panelState === 'minimized' && (
          <div className="panel-hint">
            ↑ {t('map.panel.swipeUp')}
          </div>
        )}
        {panelState === 'default' && (
          <div className="panel-hint">
            ↕ {t('map.panel.swipe')}
          </div>
        )}
        {panelState === 'expanded' && (
          <div className="panel-hint">
            ↓ {t('map.panel.swipeDown')}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListPanel;
